import React from "react";

import { SliderItem } from "../../features/auth/store/types/slider-types";

interface SliderCardProps {
  item: SliderItem;
  onEdit?: (item: SliderItem) => void;
  onDelete?: (item: SliderItem) => void;
}

export const SliderCard: React.FC<SliderCardProps> = ({
  item,
  onEdit,
  onDelete,
}) => {
  return (
    <div className="flex items-center gap-4 p-3 rounded-lg border border-gray-200 bg-white dark:text-white dark:bg-[#FFFFFF1A] dark:border-[#FFFFFF1A]">
      <div className="flex gap-2">
        <div>
          <div className="text-[10px] text-gray-500 mb-1">Large/Medium</div>
          <img
            src={item.desktop_image_url ?? "/placeholder.png"}
            alt={item.title ?? "slider"}
            className="w-24 h-14 object-cover rounded-md"
          />
        </div>
        <div>
          <div className="text-[10px] text-gray-500 mb-1">Small</div>
          <img
            src={
              item.mobile_image_url ??
              item.desktop_image_url ??
              "/placeholder.png"
            }
            alt={item.title ?? "slider mobile"}
            className="w-24 h-14 object-cover rounded-md"
          />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
          {item.title}
        </div>
        <a
          href={item.url || "#"}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-blue-600 break-all"
        >
          {item.url}
        </a>
      </div>
      <div className="flex gap-2">
        <button
          className="px-3 py-1 bg-yellow-50 text-yellow-700 border border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-500 dark:border-yellow-900/30 rounded text-xs"
          onClick={() => onEdit?.(item)}
        >
          Edit
        </button>
        <button
          className="px-3 py-1 bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-900/30 rounded text-xs"
          onClick={() => onDelete?.(item)}
        >
          Delete
        </button>
      </div>
    </div>
  );
};

export const SliderList: React.FC<{
  items: SliderItem[];
  onEdit?: (item: SliderItem) => void;
  onDelete?: (item: SliderItem) => void;
}> = ({ items, onEdit, onDelete }) => {
  const hasItems = items && items.length > 0;

  return (
    <div className="overflow-hidden bg-white dark:bg-zinc-900 rounded-lg border border-gray-200 dark:border-zinc-800">
      <table className="min-w-full text-left border-collapse">
        {/* Table Header */}
        <thead className="hidden md:table-header-group bg-gray-50 dark:bg-zinc-800/50 text-xs uppercase text-gray-500 dark:text-zinc-400">
          <tr>
            <th className="px-4 py-3 font-semibold">Title / Link</th>
            <th className="px-4 py-3 font-semibold">Large Screen Image</th>
            <th className="px-4 py-3 font-semibold">Small Screen Image</th>
            <th className="px-4 py-3 font-semibold text-right">Actions</th>
          </tr>
        </thead>

        {/* Table Body */}
        <tbody className="block md:table-row-group divide-y divide-gray-100 dark:divide-zinc-800">
          {hasItems ? (
            items.map((item) => (
              <tr
                key={item.id}
                className="block md:table-row text-sm text-gray-800 dark:text-zinc-300 p-4 md:p-0 space-y-2 md:space-y-0"
              >
                {/* Title and Link Cell */}
                <td className="flex flex-col md:table-cell md:px-4 md:py-3 border-b border-gray-50 md:border-none pb-2 md:pb-3">
                  <span className="md:hidden font-medium text-xs text-gray-500 dark:text-zinc-400 mb-1">
                    Title & Link
                  </span>
                  <div className="min-w-0">
                    <span className="font-semibold md:font-medium text-gray-900 dark:text-white block truncate">
                      {item.title}
                    </span>
                    <a
                      href={item.url || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 dark:text-blue-400 break-all hover:underline"
                    >
                      {item.url || "No Link Provided"}
                    </a>
                  </div>
                </td>

                {/* Large Screen Image Cell */}
                <td className="flex justify-between items-center md:table-cell md:px-4 md:py-3 border-b border-gray-50 md:border-none pb-2 md:pb-3">
                  <span className="md:hidden font-medium text-gray-500 dark:text-zinc-400">
                    Large / Medium Image
                  </span>
                  <div className="flex items-center md:justify-start">
                    <img
                      src={item.desktop_image_url ?? "/placeholder.png"}
                      alt={item.title ?? "slider desktop"}
                      className="w-24 h-14 object-cover rounded-md border border-gray-100 dark:border-zinc-800"
                    />
                  </div>
                </td>

                {/* Small Screen Image Cell */}
                <td className="flex justify-between items-center md:table-cell md:px-4 md:py-3 border-b border-gray-50 md:border-none pb-2 md:pb-3">
                  <span className="md:hidden font-medium text-gray-500 dark:text-zinc-400">
                    Small Screen Image
                  </span>
                  <div className="flex items-center md:justify-start">
                    <img
                      src={
                        item.mobile_image_url ??
                        item.desktop_image_url ??
                        "/placeholder.png"
                      }
                      alt={item.title ?? "slider mobile"}
                      className="w-24 h-14 object-cover rounded-md border border-gray-100 dark:border-zinc-800"
                    />
                  </div>
                </td>

                {/* Actions Cell */}
                <td className="flex justify-between items-center md:table-cell md:px-4 md:py-3 pt-2 md:pt-3">
                  <span className="md:hidden font-medium text-gray-500 dark:text-zinc-400">
                    Actions
                  </span>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      className="px-3 py-1 text-xs rounded bg-yellow-50 text-yellow-700 border border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-500 dark:border-yellow-900/30 hover:dark:bg-yellow-900/30 transition-colors"
                      onClick={() => onEdit?.(item)}
                    >
                      Edit
                    </button>
                    <button
                      className="px-3 py-1 text-xs rounded bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-900/30 hover:dark:bg-red-900/30 transition-colors"
                      onClick={() => onDelete?.(item)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr className="block md:table-row">
              <td
                colSpan={4}
                className="block md:table-cell px-4 py-6 text-center text-sm text-gray-500 dark:text-zinc-500"
              >
                No sliders available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
